import moment from "moment-timezone";
import {
  currentBusinessDateKey,
  getBusinessDateRange,
  getCurrentOperationalWindow,
  getLocationId,
  formatBusinessHoursLabel,
  formatAllLocationsHoursLabel,
  isWithinLocationOperationalWindow,
} from "./locationHours";

export const APP_TIMEZONE = "America/Chicago";
export const APP_DATE_TIME_FORMAT = "MM/DD/YYYY hh:mm A";
export { getLocationId, formatBusinessHoursLabel, formatAllLocationsHoursLabel };

moment.tz.setDefault(APP_TIMEZONE);

export const now = () => moment.tz(APP_TIMEZONE);

export const today = () => now().clone().startOf("day").toDate();

/** Calendar date (midnight CT) for the current location's business day. */
export const currentBusinessDate = () =>
  moment.tz(currentBusinessDateKey(now()), "YYYY-MM-DD", APP_TIMEZONE).startOf("day").toDate();

export const defaultDateRange = () => {
  const day = currentBusinessDate();
  return { start: startOfDay(day), end: endOfDay(day) };
};

export const startOfDay = (date) => moment.tz(date, APP_TIMEZONE).startOf("day").toDate();

export const endOfDay = (date) => moment.tz(date, APP_TIMEZONE).endOf("day").toDate();

export const startOfMonth = (date) => moment.tz(date, APP_TIMEZONE).startOf("month").toDate();

export const endOfMonth = (date) => moment.tz(date, APP_TIMEZONE).endOf("month").toDate();

export const startOfWeek = (date) => moment.tz(date, APP_TIMEZONE).startOf("week").toDate();

export const endOfWeek = (date) => moment.tz(date, APP_TIMEZONE).endOf("week").toDate();

export const startOfYear = (date) => moment.tz(date, APP_TIMEZONE).startOf("year").toDate();

export const endOfYear = (date) => moment.tz(date, APP_TIMEZONE).endOf("year").toDate();

export const addMonths = (date, amount) =>
  moment.tz(date, APP_TIMEZONE).add(amount, "months").toDate();

export const subMonths = (date, amount) =>
  moment.tz(date, APP_TIMEZONE).subtract(amount, "months").toDate();

export const subDays = (date, amount) =>
  moment.tz(date, APP_TIMEZONE).subtract(amount, "days").toDate();

export const subWeeks = (date, amount) =>
  moment.tz(date, APP_TIMEZONE).subtract(amount, "weeks").toDate();

export const subYears = (date, amount) =>
  moment.tz(date, APP_TIMEZONE).subtract(amount, "years").toDate();

export const isSameMonth = (date1, date2) =>
  moment.tz(date1, APP_TIMEZONE).isSame(moment.tz(date2, APP_TIMEZONE), "month");

export const isSameDay = (date1, date2) =>
  moment.tz(date1, APP_TIMEZONE).isSame(moment.tz(date2, APP_TIMEZONE), "day");

export const parseAppDateTime = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return moment.tz(value, "YYYY-MM-DD", APP_TIMEZONE).startOf("day");
  }

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) &&
    !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)
  ) {
    const local = moment.tz(value, ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm"], APP_TIMEZONE);
    if (local.isValid()) return local;
  }

  if (value instanceof Date) {
    const utc = moment.utc(value);
    const isUtcMidnight =
      utc.hour() === 0 &&
      utc.minute() === 0 &&
      utc.second() === 0 &&
      utc.millisecond() === 0;

    if (isUtcMidnight) {
      return moment.tz(utc.format("YYYY-MM-DD"), "YYYY-MM-DD", APP_TIMEZONE).startOf("day");
    }

    return moment.tz(value, APP_TIMEZONE);
  }

  if (typeof value === "number") {
    return moment.tz(value, APP_TIMEZONE);
  }

  const iso = moment(value, moment.ISO_8601, true);
  if (iso.isValid()) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
      return moment.tz(String(value), "YYYY-MM-DD", APP_TIMEZONE).startOf("day");
    }
    const utc = iso.clone().utc();
    if (
      utc.hour() === 0 &&
      utc.minute() === 0 &&
      utc.second() === 0 &&
      utc.millisecond() === 0
    ) {
      return moment.tz(utc.format("YYYY-MM-DD"), "YYYY-MM-DD", APP_TIMEZONE).startOf("day");
    }
    return iso.tz(APP_TIMEZONE);
  }

  const formatted = moment.tz(value, APP_DATE_TIME_FORMAT, true, APP_TIMEZONE);
  if (formatted.isValid()) {
    return formatted;
  }

  const parsed = moment.tz(value, APP_TIMEZONE);
  return parsed.isValid() ? parsed : null;
};

export const formatQueryDateRange = (startDate, endDate) => ({
  startDate: moment.tz(startDate, APP_TIMEZONE).format("YYYY-MM-DD"),
  endDate: moment.tz(endDate, APP_TIMEZONE).format("YYYY-MM-DD"),
});

/** YYYY-MM-DD in Central Time — never use toISOString() for business dates. */
export const formatApiDate = (date) =>
  moment.tz(date, APP_TIMEZONE).format("YYYY-MM-DD");

export const isDateTimeInRange = (value, rangeStart, rangeEnd) => {
  const date = parseAppDateTime(value);
  if (!date) return false;

  const { start, end } = getBusinessDateRange(rangeStart, rangeEnd);
  return date.isBetween(moment(start), moment(end), null, "[]");
};

export const isWithinInterval = (date, interval) => {
  const d = parseAppDateTime(date);
  if (!d) return false;

  const start = moment.tz(interval.start, APP_TIMEZONE).startOf("day");
  const end = moment.tz(interval.end, APP_TIMEZONE).endOf("day");
  return d.isSameOrAfter(start, "day") && d.isSameOrBefore(end, "day");
};

export const eachDayOfInterval = (interval) => {
  const days = [];
  const current = moment.tz(interval.start, APP_TIMEZONE).startOf("day");
  const end = moment.tz(interval.end, APP_TIMEZONE).startOf("day");

  while (current.isSameOrBefore(end, "day")) {
    days.push(current.toDate());
    current.add(1, "day");
  }

  return days;
};

export const formatDate = (date, formatStr) => {
  const m = moment.tz(date, APP_TIMEZONE);

  if (formatStr === "MMM yyyy") return m.format("MMM YYYY");
  if (formatStr === "MM/dd/yyyy") return m.format("MM/DD/YYYY");
  if (formatStr === "d") return m.format("D");
  return m.format("ddd MMM DD YYYY");
};

/** Current location operational day in Central Time. */
export const getOperationalWindow = () => getCurrentOperationalWindow(now());

export const isWithinOperationalWindow = () => isWithinLocationOperationalWindow(now());

/** Analytics API timestamps are always in CST, e.g. "2026-07-26 09:30 AM" */
export const parseAnalyticsTimestamp = (timestamp) =>
  moment.tz(timestamp, "YYYY-MM-DD hh:mm A", APP_TIMEZONE);

export const formatInAppTz = (date, format = "MM/DD/YYYY hh:mm A") => {
  const parsed = parseAppDateTime(date);
  return parsed ? parsed.format(format) : "";
};

export const toDatetimeLocalValue = (value) => {
  const parsed = parseAppDateTime(value);
  return parsed ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

export const formatUtcInAppTz = (dateString, format = "MM/DD/YYYY hh:mm A") =>
  moment.utc(dateString).tz(APP_TIMEZONE).format(format);

export const toCalendarDateKey = (value) => {
  if (!value) return null;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const parsed = moment.isMoment(value)
    ? value.clone().tz(APP_TIMEZONE)
    : moment.tz(value, APP_TIMEZONE);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

export default moment;
